import { graphql } from 'gatsby';
import * as React from 'react';
import { Boxes, BoxTitle, LinkBoxes } from '../components/Box';
import { BrandBox } from '../components/BrandBox';
import { ContactLink } from '../components/Button';
import { Container } from '../components/Container';
import { PrimaryLink } from '../components/Link';
import {
  ArticleSection,
  ContentWrapper,
  FirstSection,
  SecondSection
} from '../components/Sections';
import { IDefaultPropsWithData } from '../IDefaultProps';
import { Base } from '../layouts/Base';
import { ContentfulFrontpage, Query } from '../types/graphql-types';
import { getBoxBackground } from '../utils/getBoxBackground';
import { GetLinkFromId } from '../utils/getLinkFromId';
import { SEO } from '../utils/seo';
import { getTranslations } from '../utils/translations';

interface IPage {
  page: ContentfulFrontpage;
  translations: Map<string, string>
}

const FrontPage = ({ page, translations }: IPage) => (
  <ContentWrapper>
    <FirstSection>
      <BrandBox />

      {(page.name || page.preamble) && (
        <ArticleSection>
          <h1>{page.name}</h1>

          {page.preamble && (
            <div
              dangerouslySetInnerHTML={{
                __html: page!.preamble!.childContentfulRichText!.html as string
              }}
            />
          )}

          {page.link && GetLinkFromId(page.link.id) && (
            <PrimaryLink bold={true} to={GetLinkFromId(page.link.id) as string}>
              {translations.get("ReadMore")}
            </PrimaryLink>
          )}
        </ArticleSection>
      )}
    </FirstSection>
    <SecondSection>
      {page.boxes &&
        page.boxes.map(item => {
          if (!item || !item.id) {
            return null;
          }

          if (GetLinkFromId(item.id)) {
            return (
              <LinkBoxes
                to={GetLinkFromId(item.id)}
                key={item.id}
                backgroundColor={getBoxBackground(item)}
                mode={item.mode as boolean | undefined}
              >
                <BoxTitle>{item.name}</BoxTitle>
              </LinkBoxes>
            );
          }

          return (
            <Boxes
              key={item.id}
              backgroundColor={getBoxBackground(item)}
              mode={item.mode as boolean | undefined}
            >
              <BoxTitle>{item.name}</BoxTitle>
            </Boxes>
          );
        })}
    </SecondSection>
    {page.contactPage &&
      page.contactPage.id &&
      page.contactPage.name &&
      (<div>a</div> && GetLinkFromId(page.contactPage.id) && (
        <ContactLink to={GetLinkFromId(page.contactPage.id) as string}>
          {page.contactPage.name}
        </ContactLink>
      ))}
  </ContentWrapper>
);

export default ({ data }: IDefaultPropsWithData) => {
  const page = { ...data.contentfulFrontpage! };
  const translations = getTranslations(data)
  return (
    <Base>
      <SEO
        title={page.name as string}
        description={
          (page.preamble &&
            (page.preamble!.childContentfulRichText!.html as string)) ||
          undefined
        }
      />
      <Container>
        <FrontPage page={page} translations={translations} />
      </Container>
    </Base>
  );
};

export const query = graphql`
  query($id: String!, $locale: String!) {
    contentfulFrontpage(id: { eq: $id }) {
      name
      preamble {
        childContentfulRichText {
          html
        }
      }
      link {
        id
      }
      contactPage {
        id
        name
      }
      boxes {
        __typename
        ... on Node {
          ... on ContentfulBox {
            id
            name
            color
            mode
          }
          ... on ContentfulService {
            id
            name
            color
            mode
          }
          ... on ContentfulPage {
            id
            name
          }
        }
      }
    }
    allContentfulTranslation(filter: {key: {in: ["ReadMore"]} node_locale: {eq: $locale}}) {
      edges {
        node {
          key
          value
        }
      }
    }
  }
`;
